import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Chip, Collapse, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import {
  BentoCategory,
  FollowUpQuestionOption,
  NicheFollowUpQuestion,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { fetcherAuth } from "utils/api";

import QuestionChip from "../../QuestionChip";

interface IProps {
  goNext: (e?: CustomEvent) => void;
  categoriesToPickFrom: BentoCategory[];
  selectedCategory: BentoCategory | null;
  setSelectedCategory: Dispatch<SetStateAction<BentoCategory | null>>;
  followUpQuestions: NicheFollowUpQuestion[] | null;
  setFollowUpQuestions: Dispatch<
    SetStateAction<NicheFollowUpQuestion[] | null>
  >;
  selectedFollowupAnswers: {
    [key in number]: FollowUpQuestionOption[];
  };
  setSelectedFollowupAnswers: Dispatch<
    SetStateAction<{
      [key in number]: FollowUpQuestionOption[];
    }>
  >;
}

export default function NewNicheQuestions({
  goNext,
  categoriesToPickFrom,
  selectedCategory,
  setSelectedCategory,
  followUpQuestions,
  setFollowUpQuestions,
  selectedFollowupAnswers,
  setSelectedFollowupAnswers,
}: IProps) {
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const [loadingFollowUpQuestions, setLoadingFollowUpQuestions] =
    useState<boolean>(false);

  const fetchFollowUpQuestions = async (category: BentoCategory) => {
    setLoadingFollowUpQuestions(true);
    try {
      const { followUpQuestions } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/${category?.id}/follow-up-questions`,
        "GET",
      );
      setFollowUpQuestions(followUpQuestions);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoadingFollowUpQuestions(false);
    }
  };

  const optionIsSelected = (
    followUpQuestion: NicheFollowUpQuestion,
    option: FollowUpQuestionOption,
  ) => {
    return selectedFollowupAnswers[followUpQuestion.id]?.some(
      (selectedOption) => selectedOption.option === option.option,
    );
  };

  const nextButtonIsDisabled = () => {
    if (!selectedCategory) {
      return true;
    }

    if (!followUpQuestions || followUpQuestions.length === 0) {
      return false;
    }

    for (const followUpQuestion of followUpQuestions) {
      if (
        !selectedFollowupAnswers[followUpQuestion.id] ||
        selectedFollowupAnswers[followUpQuestion.id].length === 0
      ) {
        return true;
      }
    }
    return false;
  };

  return (
    <>
      <Box>
        <Typography variant="h6" paragraph>
          What niche do you want to pitch to?
        </Typography>
      </Box>
      <Box display="flex" gap={1} sx={{ mb: 2 }} flexWrap="wrap">
        {categoriesToPickFrom.map((category) => (
          <Chip
            icon={
              category.id === selectedCategory?.id ? (
                <Box
                  sx={{
                    background: "white",
                    width: "20px",
                    height: "20px",
                    ml: "8px !important",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    component="span"
                    sx={{ color: "primary.main" }}
                    className="fa-regular fa-check"
                  />
                </Box>
              ) : undefined
            }
            variant={
              category.id === selectedCategory?.id ? "filled" : "outlined"
            }
            color={category.id === selectedCategory?.id ? "primary" : "default"}
            onClick={() => {
              setSelectedFollowupAnswers({});
              setFollowUpQuestions(null);
              if (category.id === selectedCategory?.id) {
                setSelectedCategory(null);
              } else {
                setSelectedCategory(category);
                fetchFollowUpQuestions(category);
              }
            }}
            key={category.id}
            label={category.name}
          />
        ))}
      </Box>
      <Collapse in={!!followUpQuestions && followUpQuestions.length > 0}>
        {followUpQuestions?.map((followUpQuestion) => (
          <Box key={followUpQuestion.id} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="h6">{followUpQuestion.question}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary" paragraph>
                {followUpQuestion.description}
              </Typography>
            </Box>
            <Box display="flex" gap={1} sx={{ mb: 2 }} flexWrap="wrap">
              {followUpQuestion.options.map(
                (option: FollowUpQuestionOption) => {
                  const isSelected = optionIsSelected(followUpQuestion, option);
                  return (
                    <QuestionChip
                      isSelected={isSelected}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedFollowupAnswers((prev) => {
                            const copy = { ...prev };
                            copy[followUpQuestion.id] = copy[
                              followUpQuestion.id
                            ].filter((v) => v.option !== option.option);
                            return copy;
                          });
                        } else {
                          setSelectedFollowupAnswers((prev) => {
                            const copy = { ...prev };
                            copy[followUpQuestion.id] = [
                              ...(copy[followUpQuestion.id] || []),
                              option,
                            ];
                            return copy;
                          });
                        }
                      }}
                      key={option.option}
                      label={option.option}
                    />
                  );
                },
              )}
            </Box>
          </Box>
        ))}
      </Collapse>
      <Box display="flex" justifyContent="flex-end">
        <LoadingButton
          disableElevation
          loading={loadingFollowUpQuestions}
          disabled={nextButtonIsDisabled()}
          onClick={goNext}
          variant="contained"
        >
          Next
        </LoadingButton>
      </Box>
    </>
  );
}
